import DOMPurify from 'dompurify';

/**
 * Escape HTML special characters to prevent XSS.
 * Uses a simple character replacement approach that works in all contexts.
 */
function escapeHtml(text: string): string {
  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  };
  return text.replace(/[&<>"']/g, m => map[m] || m);
}

/**
 * Format inline markdown (bold, italic) in text.
 * Escapes HTML first to prevent XSS, then applies markdown formatting.
 */
function formatInlineMarkdown(text: string): string {
  // Escape HTML first
  let formatted = escapeHtml(text);
  // Handle bold (**text**)
  formatted = formatted.replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold">$1</strong>');
  // Handle italic (*text*) - but not if it's part of **bold**
  formatted = formatted.replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>');
  return formatted;
}

/**
 * Format markdown content for display with XSS protection.
 *
 * This is a basic markdown formatter that handles common markdown syntax.
 * For more advanced features (code blocks, tables, etc.), consider using
 * a full markdown library like react-markdown or marked.
 *
 * @param content - Raw markdown content to format
 * @returns Sanitized HTML string safe for use with dangerouslySetInnerHTML
 */
export function formatMarkdown(content: string): string {
  const lines = content.split('\n');
  let inList = false;
  const result: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // Headers (with IDs for scrolling and highlighting)
    if (trimmed.startsWith('# ')) {
      if (inList) {
        result.push('</ul>');
        inList = false;
      }
      const title = trimmed.slice(2);
      const id = titleToId(title);
      result.push(
        `<h1 id="${id}" class="text-2xl font-bold mt-6 mb-3 text-foreground">${escapeHtml(title)}</h1>`,
      );
      continue;
    }
    if (trimmed.startsWith('## ')) {
      if (inList) {
        result.push('</ul>');
        inList = false;
      }
      const title = trimmed.slice(3);
      const id = titleToId(title);
      result.push(
        `<h2 id="${id}" class="text-xl font-semibold mt-5 mb-2 text-foreground">${escapeHtml(title)}</h2>`,
      );
      continue;
    }
    if (trimmed.startsWith('### ')) {
      if (inList) {
        result.push('</ul>');
        inList = false;
      }
      const title = trimmed.slice(4);
      const id = titleToId(title);
      result.push(
        `<h3 id="${id}" class="text-lg font-medium mt-4 mb-2 text-foreground">${escapeHtml(title)}</h3>`,
      );
      continue;
    }

    // Lists
    if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      if (!inList) {
        result.push('<ul class="list-disc list-inside mb-4 space-y-1">');
        inList = true;
      }
      let listItem = trimmed.slice(2);
      // Handle bold in list items
      listItem = formatInlineMarkdown(listItem);
      result.push(`<li class="text-foreground">${listItem}</li>`);
      continue;
    }

    // Close list if we're not in one anymore
    if (inList && trimmed !== '') {
      result.push('</ul>');
      inList = false;
    }

    // Horizontal rules (---, ***, ___)
    if (/^[-*_]{3,}$/.test(trimmed)) {
      result.push('<hr class="my-6 border-t border-border" />');
      continue;
    }

    // Empty lines
    if (trimmed === '') {
      result.push('<br />');
      continue;
    }

    // Regular paragraphs
    let paragraph = line;
    paragraph = formatInlineMarkdown(paragraph);
    result.push(`<p class="mb-2 text-foreground leading-relaxed">${paragraph}</p>`);
  }

  if (inList) {
    result.push('</ul>');
  }

  const html = result.join('');
  // Sanitize the HTML to prevent XSS attacks
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['h1', 'h2', 'h3', 'p', 'ul', 'li', 'strong', 'em', 'br', 'hr'],
    ALLOWED_ATTR: ['class', 'id'], // Allow id for section anchors and highlighting
  });
}

/**
 * Convert a section title to a URL-safe ID for anchoring and highlighting.
 * Removes special characters and converts to lowercase kebab-case.
 */
export function titleToId(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special chars
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Remove duplicate hyphens
    .replace(/^-|-$/g, ''); // Trim hyphens from start/end
}
